import api from './api';

export interface CreatePlayerData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'male' | 'female' | 'other';
  contactNumber?: string;
  address?: string;
  city?: string;
  country?: string;
  primarySport?: string;
  secondarySport?: string;
  academyName?: string;
  level: 'beginner' | 'intermediate' | 'advanced' | 'professional';
  ageCategory: 'U9' | 'U11' | 'U13' | 'U15' | 'U17' | 'U19' | 'senior' | 'elder';
  dominantHand?: 'left' | 'right';
  height?: number;
  weight?: number;
  emergencyContact?: string;
  medicalConditions?: string;
  notes?: string;
}

export interface CreateCoachData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  contactNumber?: string;
  address?: string;
  city?: string;
  country?: string;
  primarySport?: string;
  academyName?: string;
  isPersonalTrainer?: boolean;
  specialization?: string;
  experience?: number;
  certifications?: string[];
  bio?: string;
  hourlyRate?: number;
  availability?: string;
  notes?: string;
}

export interface PerformanceData {
  playerId: string;
  evaluatorId?: string;
  shotTypeId: string;
  courtGridId: string;
  attempted: number;
  successful: number;
  accuracyScore: number;
  speedScore: number;
  placementScores?: Array<{ gridZone: string; count: number }>;
  comments?: string;
  sessionType?: 'practice' | 'training' | 'match' | 'tournament';
  sessionDate?: string;
  duration?: number;
  notes?: string;
}

export interface BulkPlayerData {
  players: CreatePlayerData[];
}

export interface Player {
  id: string;
  userId: string;
  playerIdCode?: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    lastLoginAt?: string | null;
  };
  dateOfBirth?: string;
  gender?: string;
  level: string;
  ageCategory: string;
  country?: string;
  primarySport?: string;
  secondarySport?: string;
  academyName?: string;
  dominantHand?: string;
  height?: number;
  weight?: number;
  canGeneratePassword?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Coach {
  id: string;
  userId: string;
  coachIdCode?: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
  };
  primarySport?: string;
  specialization?: string;
  experience?: number;
  country?: string;
  academyName?: string;
  isPersonalTrainer?: boolean;
  bio?: string;
  phone?: string;
  hourlyRate?: number;
  /** From DB is_verified */
  isVerified?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SystemStats {
  users: {
    total: number;
    players: number;
    coaches: number;
    admins: number;
  };
  evaluations: {
    total: number;
    recent: number;
  };
  system: {
    uptime: number;
    environment: string;
    timestamp: string;
  };
}

/** Express returns DB rows (snake_case, no joined user). UI expects nested user + camelCase. */
function normalizeAdminPlayer(row: Record<string, unknown>): Player {
  const uid = String(row.user_id ?? row.userId ?? '');
  const nested = row.user as Player['user'] | undefined;
  return {
    id: String(row.id ?? ''),
    userId: uid,
    playerIdCode: (row.player_id_code as string) ?? (row.playerIdCode as string) ?? undefined,
    user: nested ?? {
      id: uid,
      email: String((row as { email?: string }).email ?? ''),
      firstName: String(row.first_name ?? row.firstName ?? ''),
      lastName: String(row.last_name ?? row.lastName ?? ''),
      role: 'player',
      isActive: row.is_active !== false,
    },
    dateOfBirth: (row.date_of_birth as string) ?? (row.dateOfBirth as string) ?? undefined,
    gender: (row.gender as string) ?? undefined,
    level: String(row.skill_level ?? row.level ?? ''),
    ageCategory: String(row.age_category ?? row.ageCategory ?? ''),
    country: (row.country as string) ?? undefined,
    primarySport: (row.primary_sport as string) ?? (row.primarySport as string) ?? undefined,
    secondarySport: (row.secondary_sport as string) ?? (row.secondarySport as string) ?? undefined,
    academyName: (row.academy_name as string) ?? (row.academyName as string) ?? undefined,
    dominantHand: (row.dominant_hand as string) ?? (row.dominantHand as string) ?? undefined,
    height: (row.height as number) ?? undefined,
    weight: (row.weight as number) ?? undefined,
    canGeneratePassword: (row.can_generate_password as boolean) ?? (row.canGeneratePassword as boolean) ?? undefined,
    createdAt: String(row.created_at ?? row.createdAt ?? ''),
    updatedAt: String(row.updated_at ?? row.updatedAt ?? ''),
  };
}

function normalizeAdminCoach(row: Record<string, unknown>): Coach {
  const uid = String(row.user_id ?? row.userId ?? '');
  const nested = row.user as Coach['user'] | undefined;
  return {
    id: String(row.id ?? ''),
    userId: uid,
    coachIdCode: (row.coach_id_code as string) ?? (row.coachIdCode as string) ?? undefined,
    user: nested ?? {
      id: uid,
      email: String((row as { email?: string }).email ?? ''),
      firstName: String(row.first_name ?? row.firstName ?? ''),
      lastName: String(row.last_name ?? row.lastName ?? ''),
      role: 'coach',
      isActive: row.is_active !== false,
    },
    primarySport: (row.primary_sport as string) ?? (row.primarySport as string) ?? undefined,
    specialization: (row.specialization as string) ?? (row.specializations as string) ?? undefined,
    experience:
      (row.experience as number) ??
      (row.years_of_experience as number) ??
      undefined,
    country: (row.country as string) ?? undefined,
    academyName: (row.academy_name as string) ?? (row.academyName as string) ?? undefined,
    isPersonalTrainer: (row.is_personal_trainer as boolean) ?? (row.isPersonalTrainer as boolean) ?? undefined,
    bio: (row.bio as string) ?? undefined,
    phone: (row.phone as string) ?? undefined,
    hourlyRate: (row.hourly_rate as number) ?? (row.hourlyRate as number) ?? undefined,
    isVerified: (row.is_verified as boolean) ?? (row.isVerified as boolean) ?? false,
    createdAt: String(row.created_at ?? row.createdAt ?? ''),
    updatedAt: String(row.updated_at ?? row.updatedAt ?? ''),
  };
}

class AdminService {
  /**
   * Create a new player
   */
  async createPlayer(data: CreatePlayerData) {
    const response = await api.post('/v1/admin/players', data);
    return response.data;
  }

  /**
   * Create a new coach
   */
  async createCoach(data: CreateCoachData) {
    const response = await api.post('/v1/admin/coaches', data);
    return response.data;
  }

  /**
   * Get all players with pagination
   */
  async getPlayers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    level?: string;
    ageCategory?: string;
  }) {
    const response = await api.get<{
      success?: boolean;
      data?: unknown[];
      pagination?: unknown;
    }>('/v1/admin/players', { params });
    const body = response.data;
    const list = body?.data;
    if (Array.isArray(list)) {
      return {
        ...body,
        data: list.map((r) => normalizeAdminPlayer(r as Record<string, unknown>)),
      };
    }
    return body;
  }

  /**
   * Update a player
   */
  async updatePlayer(id: string, data: Partial<CreatePlayerData>) {
    const response = await api.put(`/v1/admin/players/${id}`, data);
    return response.data;
  }

  /**
   * Delete a player
   */
  async deletePlayer(id: string) {
    const response = await api.delete(`/v1/admin/players/${id}`);
    return response.data;
  }

  /**
   * Get all coaches with pagination
   */
  async getCoaches(params?: {
    page?: number;
    limit?: number;
    search?: string;
    specialization?: string;
  }) {
    const response = await api.get<{
      success?: boolean;
      data?: unknown[];
      pagination?: unknown;
    }>('/v1/admin/coaches', { params });
    const body = response.data;
    const list = body?.data;
    if (Array.isArray(list)) {
      return {
        ...body,
        data: list.map((r) => normalizeAdminCoach(r as Record<string, unknown>)),
      };
    }
    return body;
  }

  /**
   * Update a coach
   */
  async updateCoach(id: string, data: Partial<CreateCoachData>) {
    const response = await api.put(`/v1/admin/coaches/${id}`, data);
    return response.data;
  }

  /**
   * Delete a coach
   */
  async deleteCoach(id: string) {
    const response = await api.delete(`/v1/admin/coaches/${id}`);
    return response.data;
  }

  /**
   * Enter performance data for a player
   */
  async enterPerformanceData(data: PerformanceData) {
    const response = await api.post('/v1/admin/performance', data);
    return response.data;
  }

  /**
   * Get player performance summary
   */
  async getPlayerPerformance(playerId: string, period?: string) {
    const response = await api.get(`/v1/admin/players/${playerId}/performance`, {
      params: { period }
    });
    return response.data;
  }

  /**
   * Bulk create players
   */
  async bulkCreatePlayers(data: BulkPlayerData) {
    const response = await api.post('/v1/admin/players/bulk', data);
    return response.data;
  }

  /**
   * Get system statistics
   */
  async getSystemStats(): Promise<{ success: boolean; data: SystemStats }> {
    const response = await api.get('/v1/admin/stats');
    return response.data;
  }

  /**
   * Link player to coach
   */
  async linkPlayerToCoach(playerId: string, coachId: string, isPrimaryCoach: boolean = false) {
    const response = await api.post('/player-coaches', {
      playerId,
      coachId,
      isPrimaryCoach,
      requestedBy: 'coach'
    });
    return response.data;
  }

  /**
   * Unlink player from coach
   */
  async unlinkPlayerFromCoach(playerId: string, coachId: string) {
    const response = await api.delete(`/player-coaches/${playerId}/${coachId}`);
    return response.data;
  }

  /**
   * Get players under a coach (legacy route at /api/coaches/:id/players, proxied by nginx — not under /api/v1)
   */
  async getPlayersForCoach(coachId: string) {
    const response = await fetch(`/api/coaches/${coachId}/players`);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((body as { message?: string }).message || `HTTP ${response.status}`);
    }
    return body as { success: boolean; data?: unknown[]; message?: string };
  }
}

export default new AdminService();
